import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

import DropdownMenu, { type DropdownMenuAlign } from '@/legacy-src/components/button/DropdownMenu'
import useCompanyBenefits from '@/legacy-src/hooks/useCompanyBenefits'
import useServices from '@/legacy-src/hooks/useServices'
import { SERVICES_TYPE } from '@/legacy-src/redux/DiagnosisRedux'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { getCompanyServices } from '@/legacy-src/utilities/filterB2bServices'
import { getTranslate } from '@/legacy-src/utilities/i18n'
import isCountry from '@/legacy-src/utilities/isCountry'

type Props = {
  readonly hideCounselling?: boolean
  readonly className?: string
  readonly align?: DropdownMenuAlign
}

const ServiceDropdown: React.FC<Props> = ({ hideCounselling = false, className, align }) => {
  const router = useRouter()
  const { t } = getTranslate()
  const { quotas, hasBenefitProgram } = useCompanyBenefits()
  const { selectedServiceId, selectedService, visibleServices } = useServices()
  const showOnlyQuotaServices = useFeatureIsOn('company-show-only-quota-services')

  const companyServiceIds = useMemo(() => getCompanyServices(quotas ?? []), [quotas])

  const services = useMemo(() => {
    if (!showOnlyQuotaServices || !hasBenefitProgram || companyServiceIds.length === 0) {
      return visibleServices
    }
    return visibleServices?.filter((service) => companyServiceIds.includes(service.id))
  }, [visibleServices, showOnlyQuotaServices, hasBenefitProgram, companyServiceIds])

  useEffect(() => {
    if (!services) return

    // For Hungarian B2B users, automatically select the first company service for B2B clients.
    const isB2bInHungary = isCountry('hu') && hasBenefitProgram
    const companyServices = services.filter((service) => companyServiceIds.includes(service.id))
    if (
      isB2bInHungary &&
      services.length &&
      companyServices.length &&
      selectedServiceId !== undefined &&
      !companyServiceIds.includes(selectedServiceId)
    ) {
      router.replace({ pathname: router.pathname, query: { ...router.query, service: companyServices[0].id } })
    }
  }, [services, companyServiceIds, hasBenefitProgram, selectedServiceId, router])

  const handleOnChangeService = (option: ServiceType) => {
    const newRoute = {
      pathname: router.pathname,
      query: { ...router.query, service: option.id },
    }
    router.push(newRoute, undefined, { shallow: true })
  }

  const mainItem = {
    text: `${selectedService?.name} (${t('general.appointments.duration', { duration: selectedService?.duration })})`,
    icon: selectedService?.iconName ? selectedService.iconName : 'user',
  }

  const dropdownListItems = services
    ?.filter((item) => {
      const isItemCounselling = item?.type === SERVICES_TYPE.COUNSELLING
      const isHiddenCounselling = hideCounselling && isItemCounselling

      return !isHiddenCounselling
    })
    .map((item) => ({
      id: item.id,
      text: `${item.name} (${t('general.appointments.duration', { duration: item.duration })})`,
      value: item,
      icon: item.iconName || 'user',
      onClick: handleOnChangeService,
    }))

  if (selectedService === undefined) {
    return null
  }

  return (
    <DropdownMenu
      dataTestId="service-select"
      className={className}
      mainItem={mainItem}
      listItems={dropdownListItems}
      align={align}
    />
  )
}

export default ServiceDropdown
